import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CommonButton from "../../../components/CommonButton";
import {
  ArrowRight,
  Loader2,
  MapPin,
  Edit3,
  Plus,
  User,
  Phone,
  Check,
  Home,
  Briefcase,
} from "lucide-react";
import { useSelectAddressMutation } from "../../../redux/actions/Customer/cartCustomer";
import { useLazyGetAddressQuery } from "../../../redux/actions/Customer/profileCustomer";
import { IAddressData } from "../../../types/Response/CustomerResponse";
import AddressCard from "../../../components/customer/CartComponents.tsx/AddressCard";

const CustomerAddress: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Pick forward token structures out of navigation location state parameters
  const { orderId, pricing } = location.state || {};

  const [checkoutView, setCheckoutView] = useState<"list" | "form">("list");
  const [activeSelectedAddress, setActiveSelectedAddress] =
    useState<IAddressData | null>(null);

  const [
    triggerGetSavedAddresses,
    { data: savedAddressesResponse, isFetching: isFetchingAddresses },
  ] = useLazyGetAddressQuery();
  const [selectAddressMapping, { isLoading: isLinkingAddress }] =
    useSelectAddressMutation();

  useEffect(() => {
    triggerGetSavedAddresses();
  }, [triggerGetSavedAddresses]);

  // Set the default address if one is returned on list load
  useEffect(() => {
    if (
      savedAddressesResponse?.data &&
      savedAddressesResponse.data.length > 0
    ) {
      const primaryDefault =
        savedAddressesResponse.data.find((a: any) => a.isDefault) ||
        savedAddressesResponse.data[0];
      setActiveSelectedAddress(primaryDefault);
    }
  }, [savedAddressesResponse]);

  const savedAddressesList = savedAddressesResponse?.data || [];

  const handleSelectAlternativeAddress = async (addr: IAddressData) => {
    try {
      await selectAddressMapping({ addressId: addr.id }).unwrap();
      setActiveSelectedAddress(addr);
      setCheckoutView("list");
    } catch (err) {
      console.error("Failed to register address on order:", err);
    }
  };

  const handleFormSuccessCompletion = async () => {
    const addressesRefresh = await triggerGetSavedAddresses().unwrap();
    const addressDataList = addressesRefresh?.data || [];
    if (addressDataList.length > 0) {
      const targetAddress =
        addressDataList.find((a: any) => a.isDefault) || addressDataList[0];
      await selectAddressMapping({ addressId: targetAddress.id }).unwrap();
      setActiveSelectedAddress(targetAddress);
    }
    setCheckoutView("list");
  };

  const handleProceedToPaymentGateway = () => {
    if (!activeSelectedAddress) return;
    navigate("/cartpage/customerpaymentpage", {
      state: { orderId, pricing, selectedAddress: activeSelectedAddress },
    });
  };

  const isGlobalProcessing = isFetchingAddresses || isLinkingAddress;

  return (
    <div className="px-4 lg:px-40 relative text-left py-10 max-w-4xl mx-auto selection:bg-[#A8D97B]/30">
      {isGlobalProcessing && (
        <div className="fixed inset-0 bg-white/40 backdrop-blur-3xs z-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2F5E2E]" />
        </div>
      )}

      <div className="w-full space-y-6">
        {checkoutView === "list" ? (
          <>
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2 text-slate-900 font-bold text-sm pl-1">
                <MapPin size={16} className="text-[#2F5E2E]" />
                <h2>Select Order Delivery Destination</h2>
              </div>
              {activeSelectedAddress && (
                <button
                  onClick={() => triggerGetSavedAddresses()}
                  className="text-xs text-[#2F5E2E] font-black flex items-center gap-1 hover:underline"
                >
                  Refresh Saved Options
                </button>
              )}
            </div>

            {activeSelectedAddress ? (
              <div className="space-y-4">
                <div className="border border-slate-200/80 rounded-2xl p-5 bg-white relative shadow-3xs border-l-4 border-l-[#2F5E2E]">
                  <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-black tracking-wider bg-emerald-50 text-emerald-700 px-2.5 py-1 border border-emerald-100 rounded-full uppercase">
                    <Check size={11} className="stroke-[3]" /> Active Shipping
                    Target
                  </span>
                  <div className="space-y-1.5 pr-24">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-[#2F5E2E]" />
                      <span className="font-bold text-gray-900 text-sm">
                        {activeSelectedAddress.fullName}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 pl-5">
                      {activeSelectedAddress.addressLine1}
                    </p>
                    <p className="text-xs text-slate-500 pl-5">
                      {activeSelectedAddress.city}, UAE
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-400 pl-5 pt-1">
                      <Phone className="w-3 h-3" />
                      <span className="font-mono text-slate-600 font-bold">
                        {activeSelectedAddress.phoneNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 pl-1">
                    Alternative Saved Addresses
                  </p>
                  <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                    {savedAddressesList
                      .filter((a) => a.id !== activeSelectedAddress.id)
                      .map((address: IAddressData) => (
                        <div
                          key={address.id}
                          onClick={() =>
                            handleSelectAlternativeAddress(address)
                          }
                          className="p-4 bg-white rounded-2xl border border-slate-200 hover:border-[#2F5E2E] cursor-pointer transition-all shadow-3xs flex flex-col justify-between group"
                        >
                          <div>
                            <p className="font-bold text-gray-900 text-xs flex justify-between">
                              {address.fullName}{" "}
                              <span>
                                {address.addressType === "office" ? (
                                  <Briefcase size={12} />
                                ) : (
                                  <Home size={12} />
                                )}
                              </span>
                            </p>
                            <p className="text-xs text-slate-500 line-clamp-1 mt-1">
                              {address.addressLine1}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <button
                  onClick={() => setCheckoutView("form")}
                  className="w-full h-11 border border-dashed text-slate-600 hover:text-[#2F5E2E] hover:border-[#2F5E2E] rounded-xl text-xs font-black bg-white flex items-center justify-center gap-1.5 transition-all"
                >
                  <Plus size={14} /> Add New Address Coordinate
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-dashed text-center">
                <MapPin className="w-10 h-10 text-slate-300 mb-2" />
                <button
                  onClick={() => setCheckoutView("form")}
                  className="px-4 py-2 bg-[#2F5E2E] text-white text-xs font-black rounded-xl"
                >
                  <Plus size={14} /> Create First Delivery Address
                </button>
              </div>
            )}
          </>
        ) : (
          <AddressCard
            initialAddress={null}
            onCancelForm={() => setCheckoutView("list")}
          />
        )}
      </div>

      <div className="fixed bottom-4 right-4 z-40">
        <CommonButton
          text="Proceed to Secure Payment"
          variant="filled"
          rounded="full"
          icon={<ArrowRight size={16} className="stroke-[2.5]" />}
          iconPosition="right"
          disabled={!activeSelectedAddress || checkoutView === "form"}
          onClick={handleProceedToPaymentGateway}
          className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white font-black text-xs px-6 py-3 shadow-md"
        />
      </div>
    </div>
  );
};

export default CustomerAddress;
